/* eslint-disable react/no-array-index-key, consistent-return, @typescript-eslint/no-shadow, prettier/prettier, @typescript-eslint/no-unused-vars, @typescript-eslint/no-explicit-any */
import React from 'react';
import { useStyletron } from 'baseui';
import { Accordion, Panel } from 'baseui/accordion';
import { Notification, KIND as NOTIF_KIND } from 'baseui/notification';
import { Tag, KIND as TAG_KIND } from 'baseui/tag';
import { IImpactReport } from '../../utils/types';
import RiskBadge from '../../components/RiskBadge';
import ServiceChip from '../../components/ServiceChip';
import SegmentImpactMatrix from './SegmentImpactMatrix';
import FlowChart from '../../components/FlowChart';

interface ImpactReportProps {
	report: IImpactReport;
}

const RISK_TO_NOTIF: Record<string, string> = {
	critical: NOTIF_KIND.negative,
	high: NOTIF_KIND.negative,
	medium: NOTIF_KIND.warning,
	low: NOTIF_KIND.positive
};

const pickString = (...candidates: unknown[]): string => {
	const found = candidates.find((c) => typeof c === 'string' && (c as string).length > 0);
	return (found as string) || '';
};

const ImpactReport: React.FC<ImpactReportProps> = ({ report: rawReport }) => {
	const [css] = useStyletron();

	const r: any = rawReport || {};
	const affectedServices: any[] = r?.backend?.affectedServices || [];
	const dbOperations: any[] = r?.backend?.dbOperations || [];
	const configResponseFields: any[] = r?.frontend?.configResponseFields || [];
	const androidComponents: any[] = r?.frontend?.androidComponents || [];
	const segmentImpacts: any[] = r?.userExperience?.segmentImpacts || [];
	const multiSegmentScenarios: any[] = r?.userExperience?.multiSegmentScenarios || [];
	const universalImpact: string = r?.userExperience?.universalImpact || '';
	const businessRules: any[] = r?.businessRules || [];
	const risks: any[] = r?.risks || [];
	const flowDiagram: string = r?.flowDiagram || '';

	return (
		<div className={css({ marginTop: '24px' })}>
			<h3 className={css({ marginBottom: '16px' })}>Impact Analysis Report</h3>
			<Accordion>
				{/* 1. Backend Impact */}
				<Panel title={`Backend Impact (${affectedServices.length} ${affectedServices.length === 1 ? 'entry' : 'entries'})`}>
					<div className={css({ padding: '8px 0' })}>
						{affectedServices.map((svc, idx) => {
							const isString = typeof svc === 'string';
							const serviceName = isString ? svc : pickString((svc as any)?.service, (svc as any)?.name);
							let files: string[] = [];
							if (!isString) {
								const obj = svc as any;
								if (Array.isArray(obj?.files)) {
									files = obj.files.filter((f: unknown) => typeof f === 'string');
								} else if (obj?.file) {
									files = [String(obj.file)];
								}
							}
							const method = isString ? '' : pickString((svc as any)?.method);
							const description = isString ? '' : pickString((svc as any)?.description);
							const riskLevel = isString ? '' : pickString((svc as any)?.riskLevel);
							return (
								<div
									key={idx}
									className={css({
										border: '1px solid #E2E2E2',
										borderRadius: '8px',
										padding: '12px',
										marginBottom: '12px'
									})}>
									<div className={css({ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px', flexWrap: 'wrap' as const })}>
										{serviceName && <ServiceChip name={serviceName} />}
										{riskLevel && <RiskBadge level={riskLevel} />}
										{method && <code className={css({ fontSize: '12px', color: '#276EF1' })}>{method}</code>}
									</div>
									{files.length > 0 && (
										<ul className={css({ margin: '0 0 8px 0', paddingLeft: '20px', fontSize: '13px', color: '#555' })}>
											{files.map((file, fIdx) => (
												<li key={fIdx}>
													<code>{file}</code>
												</li>
											))}
										</ul>
									)}
									{description && (
										<p className={css({ margin: '4px 0 0 0', fontSize: '13px', color: '#555' })}>{description}</p>
									)}
								</div>
							);
						})}

						{dbOperations.length > 0 && (
							<div className={css({ marginTop: '16px' })}>
								<strong className={css({ fontSize: '14px' })}>Database Operations</strong>
								<div className={css({ marginTop: '8px' })}>
									{dbOperations.map((op, idx) => {
										const isString = typeof op === 'string';
										const collection = isString ? '' : pickString((op as any)?.collection, (op as any)?.service);
										const operation = isString ? '' : pickString((op as any)?.operation);
										const change = isString ? (op as string) : pickString((op as any)?.change, (op as any)?.description);
										return (
											<div
												key={idx}
												className={css({
													display: 'flex',
													gap: '12px',
													padding: '8px 12px',
													backgroundColor: idx % 2 === 0 ? '#FAFAFA' : '#FFF',
													borderRadius: '4px',
													fontSize: '13px',
													alignItems: 'center',
													flexWrap: 'wrap' as const
												})}>
												{collection && <code className={css({ color: '#276EF1', minWidth: '140px' })}>{collection}</code>}
												{operation && (
													<Tag closeable={false} kind={TAG_KIND.accent}>
														{operation}
													</Tag>
												)}
												{change && <span>{change}</span>}
											</div>
										);
									})}
								</div>
							</div>
						)}
					</div>
				</Panel>

				{/* 2. Frontend Impact */}
				<Panel title={`Frontend Impact (${androidComponents.length} ${androidComponents.length === 1 ? 'component' : 'components'})`}>
					<div className={css({ padding: '8px 0' })}>
						{configResponseFields.length > 0 && (
							<div className={css({ marginBottom: '12px' })}>
								<strong className={css({ fontSize: '14px' })}>Config Response Fields:</strong>
								<div className={css({ display: 'flex', gap: '8px', marginTop: '4px', flexWrap: 'wrap' as const })}>
									{configResponseFields.map((field, idx) => {
										const name = typeof field === 'string' ? field : pickString(field?.field, field?.name);
										const desc = typeof field === 'string' ? '' : pickString(field?.description);
										return (
											<div
												key={idx}
												className={css({ display: 'flex', flexDirection: 'column', gap: '2px' })}>
												<Tag closeable={false} kind={TAG_KIND.primary}>
													{name || '(unnamed)'}
												</Tag>
												{desc && <span className={css({ fontSize: '11px', color: '#666' })}>{desc}</span>}
											</div>
										);
									})}
								</div>
							</div>
						)}
						{androidComponents.map((comp, idx) => {
							const name = pickString(comp?.component);
							const screen = pickString(comp?.screen);
							const visualChange = pickString(comp?.visualChange, comp?.description);
							return (
								<div
									key={idx}
									className={css({
										border: '1px solid #E2E2E2',
										borderRadius: '8px',
										padding: '12px',
										marginBottom: '8px'
									})}>
									<div className={css({ display: 'flex', gap: '8px', marginBottom: '6px' })}>
										{name && <strong>{name}</strong>}
										{screen && <span className={css({ color: '#666' })}>on {screen}</span>}
									</div>
									{visualChange && (
										<p className={css({ margin: '0', fontSize: '13px', color: '#555' })}>{visualChange}</p>
									)}
								</div>
							);
						})}
					</div>
				</Panel>

				{/* 3. User Experience */}
				<Panel title='User Experience'>
					<div className={css({ padding: '8px 0' })}>
						<SegmentImpactMatrix impacts={segmentImpacts} />

						{multiSegmentScenarios.length > 0 && (
							<div className={css({ marginTop: '20px' })}>
								<strong className={css({ fontSize: '14px' })}>
									Multi-segment users (order-based resolution)
								</strong>
								<div className={css({ marginTop: '8px' })}>
									{multiSegmentScenarios.map((sc, idx) => {
										const added: string[] = Array.isArray(sc?.added) ? sc.added : [];
										const removed: string[] = Array.isArray(sc?.removed) ? sc.removed : [];
										const reordered: string[] = Array.isArray(sc?.reordered) ? sc.reordered : [];
										const membership = pickString(sc?.membershipExample);
										const cur = pickString(sc?.currentResolvedSegment);
										const prop = pickString(sc?.proposedResolvedSegment);
										const net = pickString(sc?.netEffect);
										return (
											<div
												key={idx}
												className={css({
													border: '1px solid #E2E2E2',
													borderRadius: '8px',
													padding: '12px',
													marginBottom: '8px',
													backgroundColor: '#FBFAFF'
												})}>
												{membership && (
													<div className={css({ fontWeight: 600, marginBottom: '6px', fontSize: '13px' })}>
														{membership}
													</div>
												)}
												{(cur || prop) && (
													<div className={css({ fontSize: '12px', color: '#555', marginBottom: '6px' })}>
														Resolves to:{' '}
														{cur && <code>{cur}</code>}
														{cur && prop && ' → '}
														{prop && <code>{prop}</code>}
													</div>
												)}
												{added.length > 0 && (
													<div className={css({ fontSize: '13px', marginBottom: '4px' })}>
														<strong className={css({ color: '#05944F' })}>+ Added: </strong>
														{added.join(', ')}
													</div>
												)}
												{removed.length > 0 && (
													<div className={css({ fontSize: '13px', marginBottom: '4px' })}>
														<strong className={css({ color: '#E11900' })}>- Removed: </strong>
														{removed.join(', ')}
													</div>
												)}
												{reordered.length > 0 && (
													<div className={css({ fontSize: '13px', marginBottom: '4px' })}>
														<strong className={css({ color: '#996300' })}>~ Reordered: </strong>
														{reordered.join('; ')}
													</div>
												)}
												{net && (
													<div
														className={css({
															marginTop: '6px',
															padding: '6px 10px',
															backgroundColor: '#F6F6F6',
															borderRadius: '4px',
															fontSize: '12px',
															color: '#444'
														})}>
														{net}
													</div>
												)}
											</div>
										);
									})}
								</div>
							</div>
						)}

						{universalImpact && (
							<div
								className={css({
									marginTop: '16px',
									padding: '12px 16px',
									backgroundColor: '#F6F6F6',
									borderRadius: '8px',
									fontSize: '14px'
								})}>
								<strong>Universal Impact: </strong>
								{universalImpact}
							</div>
						)}
					</div>
				</Panel>

				{/* 4. Business Rules */}
				<Panel title={`Business Rules (${businessRules.length})`}>
					<div className={css({ padding: '8px 0' })}>
						{businessRules.map((rule, idx) => (
							<div
								key={idx}
								className={css({
									border: '1px solid #E2E2E2',
									borderRadius: '8px',
									padding: '12px',
									marginBottom: '8px'
								})}>
								<strong className={css({ fontSize: '14px' })}>{pickString(rule?.rule)}</strong>
								<div
									className={css({
										display: 'grid',
										gridTemplateColumns: '1fr 1fr',
										gap: '8px',
										marginTop: '8px'
									})}>
									<div
										className={css({
											backgroundColor: '#F6F6F6',
											padding: '8px',
											borderRadius: '6px',
											fontSize: '13px'
										})}>
										<div className={css({ color: '#999', fontSize: '11px', marginBottom: '2px' })}>Current</div>
										{pickString(rule?.currentState)}
									</div>
									<div
										className={css({
											backgroundColor: '#E6F7ED',
											padding: '8px',
											borderRadius: '6px',
											fontSize: '13px'
										})}>
										<div className={css({ color: '#999', fontSize: '11px', marginBottom: '2px' })}>Proposed</div>
										{pickString(rule?.proposedState)}
									</div>
								</div>
							</div>
						))}
					</div>
				</Panel>

				{/* 5. Risks */}
				<Panel title={`Risks (${risks.length})`}>
					<div className={css({ padding: '8px 0' })}>
						{risks.map((risk, idx) => {
							const level = pickString(risk?.level);
							return (
								<Notification
									key={idx}
									kind={(RISK_TO_NOTIF[level] || NOTIF_KIND.info) as any}
									overrides={{
										Body: { style: { width: '100%', marginBottom: '8px' } }
									}}>
									<div>
										<div className={css({ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '4px' })}>
											{level && <RiskBadge level={level} />}
											<span className={css({ fontWeight: 600 })}>{pickString(risk?.description)}</span>
										</div>
										{risk?.mitigation && (
											<div className={css({ fontSize: '13px', color: '#555', marginTop: '4px' })}>
												<strong>Mitigation: </strong>
												{pickString(risk?.mitigation)}
											</div>
										)}
									</div>
								</Notification>
							);
						})}
					</div>
				</Panel>

				{/* 6. Flow Diagram */}
				{flowDiagram && (
					<Panel title='Flow Diagram'>
						<div className={css({ padding: '8px 0' })}>
							<FlowChart diagram={flowDiagram} />
						</div>
					</Panel>
				)}
			</Accordion>
		</div>
	);
};

export default ImpactReport;
